/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// ============================================================================================================
// CART DEBUG - 诊断购物车实时更新问题
// ============================================================================================================
// 使用方法：
//   enableCartDebug()  - 启用调试
//   disableCartDebug() - 停止调试并清理
// ============================================================================================================

(function() {
  let debugListeners = [];
  let isDebugEnabled = false;

  window.enableCartDebug = function() {
    if (isDebugEnabled) {
      console.log('⚠️ Debug already enabled. Call disableCartDebug() first.');
      return;
    }

    console.log('=== Cart Debug Enabled ===');
    isDebugEnabled = true;

    // 1. 检查 cart-count 是否正确定义
    console.log('cart-count defined:', customElements.get('cart-count') !== undefined);
    console.log('cart-count elements:', document.querySelectorAll('cart-count').length);

    // 2. 同时监听 document 和 documentElement
    ['variant:added', 'cart:show-and-fetch', 'cart:updated'].forEach(name => {
      // 监听 document
      const docHandler = (e) => console.log(`✅ ${name} on document`, e.detail);
      document.addEventListener(name, docHandler);
      debugListeners.push({ target: document, name, handler: docHandler });

      // 监听 documentElement
      const elemHandler = (e) => console.log(`✅ ${name} on documentElement`, e.detail);
      document.documentElement.addEventListener(name, elemHandler);
      debugListeners.push({ target: document.documentElement, name, handler: elemHandler });
    });

    console.log('Ready. Add a product to cart.');
    console.log('To stop: disableCartDebug()');
  };

  window.disableCartDebug = function() {
    if (!isDebugEnabled) {
      console.log('⚠️ Debug not enabled.');
      return;
    }

    debugListeners.forEach(({ target, name, handler }) => {
      target.removeEventListener(name, handler);
    });
    debugListeners = [];
    isDebugEnabled = false;
    console.log('=== Cart Debug Disabled ===');
  };
})();

// ============================================================================================================
// 修复购物车实时更新 - 补齐缺失的 sections 数据
// ============================================================================================================
// 根本原因：cart:refresh 事件的 sections 字段为 undefined，导致 theme.js _rerenderCart 报错
// 修复策略：在捕获阶段拦截 cart:refresh，检测到 sections 缺失时补齐数据并重新派发事件
// ============================================================================================================

(function() {
  function init() {
    // 在 DOM 加载后重新查询元素
    const miniCartEl = document.querySelector('cart-drawer') || document.getElementById('mini-cart');
    if (!miniCartEl) {
      console.warn('[Cart Fix] cart-drawer not found');
      return;
    }

    // 一次性获取有效的 section 名单（性能优化）
    const validSections = window.themeVariables?.routes?.cartSections || ['mini-cart'];

    document.documentElement.addEventListener('cart:refresh', async function(event) {
      // 哨兵：跳过已修复的事件
      if (event.detail?.__miniCartFixed) return;

      // 只在 sections 缺失时介入
      if (!event.detail?.cart?.sections) {
        // 提前缓存 event.detail (Safari 异步后会置空)
        const detail = { ...event.detail };
        const cart = { ...detail.cart };

        event.stopImmediatePropagation(); // 阻止 theme.js _rerenderCart 报错

        // 动态获取 section ID
        const sectionId = miniCartEl.getAttribute('section');

        // 校验 section ID
        if (!sectionId || !validSections.includes(sectionId)) {
          console.warn('[Cart Fix] Invalid section ID:', sectionId);
          // 派发原始事件（带哨兵），让其他逻辑继续
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { ...detail, __miniCartFixed: true }
          }));
          return;
        }

        try {
          const response = await fetch(`/?sections=${sectionId}`, {
            credentials: 'same-origin',
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();

          // 动态查找实际存在的 section key
          const sectionKey = validSections.find(id => data[id]);
          if (!sectionKey) {
            throw new Error(`Section ${sectionId} missing in response`);
          }

          const html = data[sectionKey];

          // 验证 HTML 包含 cart-drawer
          if (!html || !html.includes('<cart-drawer')) {
            throw new Error(`Invalid section HTML for ${sectionKey}`);
          }

          // 重新派发完整事件（保留原有 sections）
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              ...detail,
              cart: {
                ...cart,
                sections: {
                  ...(cart.sections || {}),  // 保留已有的其他 sections
                  [sectionId]: html           // 只补齐缺失的这个
                }
              },
              __miniCartFixed: true  // 哨兵标记
            }
          }));
        } catch (err) {
          console.error('[Cart Fix] Failed to fetch mini-cart section:', err);
          // 派发原始事件（带哨兵），让其他逻辑继续
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { ...detail, __miniCartFixed: true }
          }));
        }
      }
    }, { capture: true });  // 捕获阶段，必须！
  }

  // DOM 就绪检测
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();  // 已经加载完成，直接执行
  }
})();
